import CareSuperLogo from "@/components/CareSuperLogo";
import ULThemeCard from "@/components/ULThemeCard";
import {
  applyMemberPortalBrand,
  MEMBER_PORTAL_COLORS,
} from "@/utils/theme/memberPortalBrand";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";

import Footer from "./components/Footer";
import Header from "./components/Header";
import MfaOtpChallengeForm from "./components/MfaOtpChallengeForm";
import { useMfaOtpChallengeManager } from "./hooks/useMfaOtpChallengeManager";

/**
 * The `mfa-otp-challenge` screen — the TOTP challenge, rendered in advanced
 * mode for the FLOW B member portal ONLY.
 *
 * ⚠️ THE CLIENT FILTER IS WHAT MAKES THIS SCREEN FLOW-B-ONLY, AND IT LIVES IN
 * TERRAFORM, NOT HERE. `auth0_prompt_screen_renderer.mfa_otp_challenge` in
 * infra/terraform/auth0.tf carries a `filters` block naming the Flow B client;
 * Flow A does not match and therefore falls back to STANDARD Universal Login.
 * That contrast is the entire point — the two portals run the same step-up
 * against the same tenant and the same screen name, and differ only in
 * invocation style (popup vs redirect) and rendering mode (standard vs ACUL).
 *
 * If this bundle ever appears in Flow A's popup, the filter is not being
 * applied. Stop and check the rendering config before drawing any conclusion
 * from a side-by-side comparison. See ARCHITECTURE.md, "The two-path
 * comparison".
 *
 * ⚠️ THIS SCREEN SERVES TWO MOMENTS AND CANNOT TELL THEM APART. Like
 * `passkey-enrollment` and `email-identifier-challenge` before it, one screen
 * name covers more than one journey:
 *
 *   login MFA  the ordinary OTP challenge at sign-in on Flow B
 *   step-up    the beneficiaries challenge (acr_values + max_age=0)
 *
 * Both are driven by the same Action and both land here. Unlike those two
 * screens there is NO discriminator available: the ACUL context's
 * `TransactionMembers` exposes state, locale, connections and errors — but not
 * `acr_values` (verified against the installed SDK's own types, not assumed).
 * `screen.data` carries only `showRememberDevice`.
 *
 * So the copy is deliberately neutral — "Verify your identity", never
 * "before viewing your beneficiaries". A step-up-specific message here would
 * be wrong every time a Flow B member simply signed in. The context that
 * explains WHY verification is being asked for belongs in the portal, which
 * does know: StepUpRedirectGate.jsx renders exactly that before redirecting.
 *
 * ⚠️ IT USES THE PORTAL'S BRAND, NOT THE SIGNUP JOURNEY'S. This screen calls
 * applyMemberPortalBrand(), NOT applyCareSuperBrand(). The signup screens
 * replicate the real CareSuper JOIN journey — green/indigo, pill buttons — and
 * are meant to look unlike the portal. This screen interrupts a member who is
 * already INSIDE the portal, so it borrows the portal's own panel treatment:
 * grey page, white rounded-xl card with a hairline border and a soft shadow,
 * near-black bold heading, teal rectangular button, Inter throughout.
 *
 * The first version got this wrong and used the signup brand, which matched
 * neither the portal nor stock Universal Login — so it read as a generic
 * hosted page with someone's colours on it. See memberPortalBrand.ts.
 *
 * Deliberately NO replica portal chrome (no fake navbar or sidebar) behind the
 * card. It would look seamless arriving from /beneficiaries, but this same
 * screen also serves Flow B's ORDINARY LOGIN MFA — where the member has not
 * entered the portal yet — and drawing a portal they are not yet inside would
 * be a lie at exactly the moment trust matters. The card makes no claim about
 * what is behind it and is therefore honest in both.
 */
function MfaOtpChallengeScreen() {
  const { mfaOtpChallenge, texts, locales } = useMfaOtpChallengeManager();

  applyAuth0Theme(mfaOtpChallenge);
  applyMemberPortalBrand();
  document.title = texts?.pageTitle || locales?.page?.title;

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center px-6 py-12"
      style={{
        backgroundColor: MEMBER_PORTAL_COLORS.pageBg,
        // Inter first (loaded via @import in index.css, matching the portal),
        // then the native UI stack rather than a bare `sans-serif`.
        //
        // ⚠️ Auth0 controls the CSP on auth.azuremark.co, so the Google Fonts
        // @import can be blocked there even though the identical request works
        // on the portal's own origin. If that happens the @import fails
        // silently, so the fallbacks are what a member actually sees — and
        // -apple-system / Segoe UI sit far closer to Inter than the default
        // serif-ish sans would.
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <div className="w-full max-w-[440px]">
        <CareSuperLogo className="h-8 w-auto mb-6" />
        {/* shadow-sm + border-gray-100 + rounded-xl is the portal's panel
         * recipe, copied from Beneficiaries.jsx so the two cannot drift by
         * eyeballing. */}
        <ULThemeCard
          className="theme-universal w-full gap-0 border border-gray-100 shadow-sm"
          style={{ borderRadius: "12px" }}
        >
          <Header />
          <MfaOtpChallengeForm />
          <Footer />
        </ULThemeCard>
      </div>
    </div>
  );
}

export default MfaOtpChallengeScreen;
